/**
 * @tyndo/hooks — typed query/mutation hooks for the predictive serving surface
 * (`/api/v1/predictive/serving/endpoints`; see
 * `Tyndo.Module.Predictive/Endpoints/PredictiveEndpoints.Serving.cs`).
 *
 * Register + list endpoints, plus the per-endpoint verb actions
 * (activate / deactivate / invoke). Verb actions vary their body per call, so
 * they are exposed as a single endpoint-agnostic action hook returning a typed
 * `mutateAsync`; cache invalidation is tenant-scoped via `invalidateApiQueries`.
 * Predictive-ui views consume these hooks only — no direct fetch.
 */

import { useApiQuery, useApiMutation } from './useApi.ts';
import { useInvalidatePredictive } from './usePredictiveModels.ts';

import {
  type ServingEndpoint,
  type PredictiveListEnvelope,
  type RegisterServingEndpointRequest,
  type ServingInvokeResult,
} from './usePredictiveTypes.ts';

export const PREDICTIVE_SERVING_ENDPOINT = '/predictive/serving/endpoints';

// ── Reads ─────────────────────────────────────────────────────────────────

/** List serving endpoints (bare array or provenance envelope). */
export function usePredictiveServingEndpoints() {
  return useApiQuery<ServingEndpoint[] | PredictiveListEnvelope<ServingEndpoint>>(
    PREDICTIVE_SERVING_ENDPOINT,
  );
}

// ── Writes ────────────────────────────────────────────────────────────────

/** Register a serving endpoint (POST /predictive/serving/endpoints). */
export function useRegisterServingEndpoint() {
  const invalidate = useInvalidatePredictive();
  return useApiMutation<ServingEndpoint, RegisterServingEndpointRequest>(
    PREDICTIVE_SERVING_ENDPOINT,
    'POST',
    { onSuccess: () => invalidate(PREDICTIVE_SERVING_ENDPOINT) },
  );
}

/**
 * Per-endpoint verb action (POST /predictive/serving/endpoints/{id}/{verb}).
 *
 * `activate` / `deactivate` / `invoke` all POST to a `{id}/{verb}` URL with a
 * body that varies per verb, so one hook instance drives every action: call
 * `mutateAsync({ endpointId, verb, body })`. The result of `invoke` carries a
 * `score`; activate/deactivate return the updated endpoint. On success the
 * serving resource prefix is invalidated so the list refreshes.
 */
export interface ServingActionVariables {
  endpointId: string;
  verb: 'activate' | 'deactivate' | 'invoke';
  body?: Record<string, unknown>;
}

export function useServingEndpointAction() {
  const invalidate = useInvalidatePredictive();
  // Descriptor-mode mutation: the URL is composed per call from the variables,
  // then the resource prefix is invalidated on settle. Returns the raw JSON so
  // an invoke caller can read `score`.
  const mutation = useApiMutation<ServingInvokeResult>();
  const mutateAsync = async (vars: ServingActionVariables): Promise<ServingInvokeResult> => {
    const result = await mutation.mutateAsync({
      method: 'POST',
      url: `${PREDICTIVE_SERVING_ENDPOINT}/${encodeURIComponent(vars.endpointId)}/${vars.verb}`,
      data: vars.body ?? {},
    });
    invalidate(PREDICTIVE_SERVING_ENDPOINT);
    return result;
  };
  return { ...mutation, mutateAsync };
}
